
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Gift, CreditCard, DollarSign, Loader2, Plus, RefreshCw, ShoppingBag, Trash2 } from 'lucide-react';
import { RedemptionOptionsConfig, GiftCardBatch, PromoCodeBatch } from '@/types/redemptionTypes';

interface PointRedemptionSettingsTabProps {
  onSaved: () => Promise<void>;
}

const formSchema = z.object({
  enableCashRedemption: z.boolean().default(true),
  enableGiftCardRedemption: z.boolean().default(true),
  enablePromoCouponRedemption: z.boolean().default(true),
  cashRedemptionMinAmount: z.coerce.number().min(5, { message: "Minimum amount must be at least $5" }),
  cashRedemptionMaxAmount: z.coerce.number().min(10, { message: "Maximum amount must be at least $10" }),
  giftCardPrefix: z.string().default("RGF-"),
  giftCardMinAmount: z.coerce.number().min(5, { message: "Minimum amount must be at least $5" }),
  giftCardMaxAmount: z.coerce.number().min(10, { message: "Maximum amount must be at least $10" }),
  promoCodeFormat: z.enum(["CODE139", "CUSTOM"]).default("CODE139"),
  promoCodeMinAmount: z.coerce.number().min(5, { message: "Minimum amount must be at least $5" }),
  promoCodeMaxAmount: z.coerce.number().min(10, { message: "Maximum amount must be at least $10" }),
  cashPointValue: z.coerce.number()
    .min(0.01, { message: "Point value must be at least 0.01" })
    .max(10, { message: "Point value must be less than 10" }),
  giftCardPointValue: z.coerce.number()
    .min(0.01, { message: "Point value must be at least 0.01" })
    .max(10, { message: "Point value must be less than 10" }),
  promoCodePointValue: z.coerce.number()
    .min(0.01, { message: "Point value must be at least 0.01" })
    .max(10, { message: "Point value must be less than 10" })
});

const PointRedemptionSettingsTab: React.FC<PointRedemptionSettingsTabProps> = ({ onSaved }) => {
  const [activeTab, setActiveTab] = useState<string>("options");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [giftCardBatches, setGiftCardBatches] = useState<GiftCardBatch[]>([]);
  const [promoCodeBatches, setPromoCodeBatches] = useState<PromoCodeBatch[]>([]);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      enableCashRedemption: true,
      enableGiftCardRedemption: true,
      enablePromoCouponRedemption: true,
      cashRedemptionMinAmount: 20,
      cashRedemptionMaxAmount: 500,
      giftCardPrefix: "RGF-",
      giftCardMinAmount: 25,
      giftCardMaxAmount: 200,
      promoCodeFormat: "CODE139",
      promoCodeMinAmount: 10,
      promoCodeMaxAmount: 100,
      cashPointValue: 0.05,
      giftCardPointValue: 0.06,
      promoCodePointValue: 0.07
    }
  });
  
  useEffect(() => {
    const loadRedemptionConfig = async () => {
      try {
        const storedConfig = localStorage.getItem('redemptionOptions');
        if (storedConfig) {
          const parsedConfig = JSON.parse(storedConfig);
          form.reset(parsedConfig);
        }
        
        const storedGiftCardBatches = localStorage.getItem('giftCardBatches');
        if (storedGiftCardBatches) {
          setGiftCardBatches(JSON.parse(storedGiftCardBatches));
        }
        
        const storedPromoCodeBatches = localStorage.getItem('promoCodeBatches');
        if (storedPromoCodeBatches) {
          setPromoCodeBatches(JSON.parse(storedPromoCodeBatches));
        }
      } catch (error) {
        console.error("Error loading redemption config:", error);
      }
    };
    
    loadRedemptionConfig();
  }, [form]);
  
  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsSaving(true);
      
      localStorage.setItem('redemptionOptions', JSON.stringify(values));
      
      await onSaved();
    } catch (error) {
      console.error("Error saving redemption options:", error);
    } finally {
      setIsSaving(false);
    }
  };
  
  const generateGiftCardBatch = () => {
    setIsGenerating(true);
    
    try {
      const prefix = form.getValues().giftCardPrefix;
      const batch: GiftCardBatch = {
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        prefix: prefix,
        codes: Array.from({ length: 50 }, (_, i) => {
          const randomNum = Math.floor(Math.random() * 10000000000).toString().padStart(10, '0');
          return `${prefix}${randomNum}`;
        }),
        isActive: true,
        used: 0
      };
      
      const updatedBatches = [...giftCardBatches, batch];
      setGiftCardBatches(updatedBatches);
      localStorage.setItem('giftCardBatches', JSON.stringify(updatedBatches));
    } catch (error) {
      console.error("Error generating gift card batch:", error);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const generatePromoCodeBatch = () => {
    setIsGenerating(true);
    
    try {
      const format = form.getValues().promoCodeFormat;
      const batch: PromoCodeBatch = {
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        format: format,
        codes: Array.from({ length: 50 }, (_, i) => {
          if (format === "CODE139") {
            return Array.from({ length: 12 }, () => Math.floor(Math.random() * 10)).join('');
          } else {
            const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
            return Array.from({ length: 12 }, () => 
              chars.charAt(Math.floor(Math.random() * chars.length))
            ).join('');
          }
        }),
        isActive: true,
        used: 0,
        storeName: "Main Store",
        isOnlineOnly: false
      };
      
      const updatedBatches = [...promoCodeBatches, batch];
      setPromoCodeBatches(updatedBatches);
      localStorage.setItem('promoCodeBatches', JSON.stringify(updatedBatches));
    } catch (error) {
      console.error("Error generating promo code batch:", error);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const toggleBatchStatus = (type: 'giftCard' | 'promoCode', id: string) => {
    if (type === 'giftCard') {
      const updatedBatches = giftCardBatches.map(batch => 
        batch.id === id ? { ...batch, isActive: !batch.isActive } : batch
      );
      setGiftCardBatches(updatedBatches);
      localStorage.setItem('giftCardBatches', JSON.stringify(updatedBatches));
    } else {
      const updatedBatches = promoCodeBatches.map(batch => 
        batch.id === id ? { ...batch, isActive: !batch.isActive } : batch
      );
      setPromoCodeBatches(updatedBatches);
      localStorage.setItem('promoCodeBatches', JSON.stringify(updatedBatches));
    }
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Point Redemption Settings</CardTitle>
          <CardDescription>
            Configure how users can redeem their points for rewards
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="options" className="flex items-center">
                <DollarSign className="mr-2 h-4 w-4" />
                Redemption Options
              </TabsTrigger>
              <TabsTrigger value="giftcards" className="flex items-center">
                <Gift className="mr-2 h-4 w-4" />
                Gift Cards
              </TabsTrigger>
              <TabsTrigger value="promocodes" className="flex items-center">
                <ShoppingBag className="mr-2 h-4 w-4" />
                Promo Codes
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="options">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">Cash Redemption</CardTitle>
                          <FormField
                            control={form.control}
                            name="enableCashRedemption"
                            render={({ field }) => (
                              <FormItem className="flex items-center space-x-2">
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                        <CardDescription>Allow users to redeem points for cash via ACH or PayPal</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <FormField
                            control={form.control}
                            name="cashPointValue"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Point Value for Cash ($)</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                    <Input 
                                      placeholder="0.05" 
                                      {...field} 
                                      className="pl-10" 
                                      step="0.01"
                                      type="number" 
                                      disabled={!form.watch("enableCashRedemption")}
                                    />
                                  </div>
                                </FormControl>
                                <FormDescription>
                                  The dollar value of a single point for cash redemption (e.g., $0.05 per point)
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={form.control}
                            name="cashRedemptionMinAmount"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Minimum Redemption Amount ($)</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                    <Input 
                                      placeholder="20" 
                                      {...field} 
                                      className="pl-10" 
                                      type="number" 
                                      disabled={!form.watch("enableCashRedemption")}
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={form.control}
                            name="cashRedemptionMaxAmount"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Maximum Redemption Amount ($)</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                    <Input 
                                      placeholder="500" 
                                      {...field} 
                                      className="pl-10" 
                                      type="number" 
                                      disabled={!form.watch("enableCashRedemption")}
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">Gift Card Redemption</CardTitle>
                          <FormField
                            control={form.control}
                            name="enableGiftCardRedemption"
                            render={({ field }) => (
                              <FormItem className="flex items-center space-x-2">
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                        <CardDescription>Allow users to redeem points for shopping gift cards</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <FormField
                            control={form.control}
                            name="giftCardPointValue"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Point Value for Gift Cards ($)</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                    <Input 
                                      placeholder="0.06" 
                                      {...field} 
                                      className="pl-10" 
                                      step="0.01"
                                      type="number" 
                                      disabled={!form.watch("enableGiftCardRedemption")}
                                    />
                                  </div>
                                </FormControl>
                                <FormDescription>
                                  The dollar value of a single point for gift card redemption (e.g., $0.06 per point)
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={form.control}
                            name="giftCardPrefix"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Gift Card Code Prefix</FormLabel>
                                <FormControl>
                                  <Input 
                                    placeholder="RGF-" 
                                    {...field} 
                                    disabled={!form.watch("enableGiftCardRedemption")}
                                  />
                                </FormControl>
                                <FormDescription>
                                  Prefix for gift card codes (e.g., RGF- for RGF-1234567890)
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="giftCardMinAmount"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Minimum Amount ($)</FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                      <Input 
                                        placeholder="25" 
                                        {...field} 
                                        className="pl-10" 
                                        type="number" 
                                        disabled={!form.watch("enableGiftCardRedemption")}
                                      />
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={form.control}
                              name="giftCardMaxAmount"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Maximum Amount ($)</FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                      <Input 
                                        placeholder="200" 
                                        {...field} 
                                        className="pl-10" 
                                        type="number" 
                                        disabled={!form.watch("enableGiftCardRedemption")}
                                      />
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">Promo Coupon Redemption</CardTitle>
                          <FormField
                            control={form.control}
                            name="enablePromoCouponRedemption"
                            render={({ field }) => (
                              <FormItem className="flex items-center space-x-2">
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                        <CardDescription>Allow users to redeem points for promo discount coupons</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <FormField
                            control={form.control}
                            name="promoCodePointValue"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Point Value for Promo Codes ($)</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                    <Input 
                                      placeholder="0.07" 
                                      {...field} 
                                      className="pl-10" 
                                      step="0.01"
                                      type="number" 
                                      disabled={!form.watch("enablePromoCouponRedemption")}
                                    />
                                  </div>
                                </FormControl>
                                <FormDescription>
                                  The dollar value of a single point for promo code redemption (e.g., $0.07 per point)
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={form.control}
                            name="promoCodeFormat"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Promo Code Format</FormLabel>
                                <Select 
                                  onValueChange={field.onChange} 
                                  defaultValue={field.value}
                                  disabled={!form.watch("enablePromoCouponRedemption")}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select a format" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="CODE139">Code 139 Format</SelectItem>
                                    <SelectItem value="CUSTOM">Custom Alphanumeric</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormDescription>
                                  Format for generated promo codes
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="promoCodeMinAmount"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Minimum Amount ($)</FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                      <Input 
                                        placeholder="10" 
                                        {...field} 
                                        className="pl-10" 
                                        type="number" 
                                        disabled={!form.watch("enablePromoCouponRedemption")}
                                      />
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={form.control}
                              name="promoCodeMaxAmount"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Maximum Amount ($)</FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                      <Input 
                                        placeholder="100" 
                                        {...field} 
                                        className="pl-10" 
                                        type="number" 
                                        disabled={!form.watch("enablePromoCouponRedemption")}
                                      />
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <Button type="submit" disabled={isSaving} className="w-full md:w-auto">
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Redemption Settings'
                    )}
                  </Button>
                </form>
              </Form>
            </TabsContent>
            
            <TabsContent value="giftcards">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Gift Card Management</CardTitle>
                      <Button 
                        onClick={generateGiftCardBatch} 
                        disabled={isGenerating || !form.watch("enableGiftCardRedemption")}
                        size="sm"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Plus className="mr-2 h-4 w-4" />
                            Generate Batch
                          </>
                        )}
                      </Button>
                    </div>
                    <CardDescription>
                      Generate and manage gift card codes for redemption
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!form.watch("enableGiftCardRedemption") ? (
                      <Alert className="bg-yellow-50 border-yellow-200">
                        <AlertDescription>
                          Gift card redemption is currently disabled. Enable it in the Redemption Options tab to manage gift cards.
                        </AlertDescription>
                      </Alert>
                    ) : giftCardBatches.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Gift className="h-12 w-12 mx-auto mb-4 opacity-20" />
                        <p>No gift card batches have been generated yet.</p>
                        <p className="text-sm mt-2">Use the Generate Batch button to create your first batch of 50 gift card codes.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {giftCardBatches.map((batch) => (
                          <Card key={batch.id} className="overflow-hidden">
                            <div className="flex items-center justify-between p-4 border-b">
                              <div>
                                <h3 className="font-medium">Batch #{batch.id.slice(-5)}</h3>
                                <p className="text-sm text-gray-500">
                                  Created: {new Date(batch.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Badge 
                                  variant={batch.isActive ? "default" : "secondary"}
                                  className="cursor-pointer"
                                  onClick={() => toggleBatchStatus('giftCard', batch.id)}
                                >
                                  {batch.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                                <span className="text-sm">
                                  Used: {batch.used}/{batch.codes.length}
                                </span>
                              </div>
                            </div>
                            <div className="p-4 bg-gray-50 text-sm">
                              <div className="flex items-center mb-2">
                                <span className="font-semibold mr-2">Prefix:</span>
                                <code className="bg-gray-200 px-1 rounded">{batch.prefix}</code>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {batch.codes.slice(0, 5).map((code, index) => (
                                  <code key={index} className="bg-gray-200 px-2 py-1 rounded text-xs">
                                    {code}
                                  </code>
                                ))}
                                {batch.codes.length > 5 && (
                                  <span className="text-xs text-gray-500">
                                    +{batch.codes.length - 5} more codes
                                  </span>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="promocodes">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Promo Code Management</CardTitle>
                      <Button 
                        onClick={generatePromoCodeBatch} 
                        disabled={isGenerating || !form.watch("enablePromoCouponRedemption")}
                        size="sm"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Plus className="mr-2 h-4 w-4" />
                            Generate Batch
                          </>
                        )}
                      </Button>
                    </div>
                    <CardDescription>
                      Generate and manage promo codes for redemption
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!form.watch("enablePromoCouponRedemption") ? (
                      <Alert className="bg-yellow-50 border-yellow-200">
                        <AlertDescription>
                          Promo code redemption is currently disabled. Enable it in the Redemption Options tab to manage promo codes.
                        </AlertDescription>
                      </Alert>
                    ) : promoCodeBatches.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <ShoppingBag className="h-12 w-12 mx-auto mb-4 opacity-20" />
                        <p>No promo code batches have been generated yet.</p>
                        <p className="text-sm mt-2">Use the Generate Batch button to create your first batch of 50 promo codes.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {promoCodeBatches.map((batch) => (
                          <Card key={batch.id} className="overflow-hidden">
                            <div className="flex items-center justify-between p-4 border-b">
                              <div>
                                <h3 className="font-medium">Batch #{batch.id.slice(-5)}</h3>
                                <p className="text-sm text-gray-500">
                                  Created: {new Date(batch.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Badge 
                                  variant={batch.isActive ? "default" : "secondary"}
                                  className="cursor-pointer"
                                  onClick={() => toggleBatchStatus('promoCode', batch.id)}
                                >
                                  {batch.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                                <span className="text-sm">
                                  Used: {batch.used}/{batch.codes.length}
                                </span>
                              </div>
                            </div>
                            <div className="p-4 bg-gray-50 text-sm">
                              <div className="flex items-center mb-2">
                                <span className="font-semibold mr-2">Format:</span>
                                <code className="bg-gray-200 px-1 rounded">
                                  {batch.format === "CODE139" ? "Code 139 Barcode" : "Custom Alphanumeric"}
                                </code>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {batch.codes.slice(0, 5).map((code, index) => (
                                  <code key={index} className="bg-gray-200 px-2 py-1 rounded text-xs">
                                    {code}
                                  </code>
                                ))}
                                {batch.codes.length > 5 && (
                                  <span className="text-xs text-gray-500">
                                    +{batch.codes.length - 5} more codes
                                  </span>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default PointRedemptionSettingsTab;
